import { Kind, visit } from "graphql";
import { mapDefinition, findDefinition, findSelectionSet, replaceFieldSelectionSet, removeField } from "./ast-path.js";
import { pruneUnusedVariableDefinitions } from "./field-selection.js";
function listFragments(doc) {
  return doc.definitions.filter((d) => d.kind === Kind.FRAGMENT_DEFINITION).map((f) => f.name.value);
}
function listFragmentInfos(doc) {
  return doc.definitions.filter((d) => d.kind === Kind.FRAGMENT_DEFINITION).map((f) => ({
    name: f.name.value,
    typeName: f.typeCondition.name.value
  }));
}
function createFragmentFromSelection(doc, path, fragmentName, typeName, target) {
  const definition = findDefinition(doc, target);
  if (!definition) {
    return doc;
  }
  const targetSelectionSet = findSelectionSet(definition.selectionSet, path);
  if (!targetSelectionSet || targetSelectionSet.selections.length === 0) {
    return doc;
  }
  const fragmentDef = {
    kind: Kind.FRAGMENT_DEFINITION,
    name: {
      kind: Kind.NAME,
      value: fragmentName
    },
    typeCondition: {
      kind: Kind.NAMED_TYPE,
      name: {
        kind: Kind.NAME,
        value: typeName
      }
    },
    directives: [],
    selectionSet: targetSelectionSet
  };
  const spread = {
    kind: Kind.FRAGMENT_SPREAD,
    name: {
      kind: Kind.NAME,
      value: fragmentName
    },
    directives: []
  };
  const spreadSet = {
    kind: Kind.SELECTION_SET,
    selections: [spread]
  };
  let changed = false;
  const updatedDoc = mapDefinition(doc, target, (def) => {
    const newSelectionSet = replaceFieldSelectionSet(def.selectionSet, path, spreadSet);
    if (newSelectionSet === def.selectionSet) {
      return def;
    }
    changed = true;
    return {
      ...def,
      selectionSet: newSelectionSet
    };
  });
  if (!changed) {
    return doc;
  }
  return {
    ...updatedDoc,
    definitions: [...updatedDoc.definitions, fragmentDef]
  };
}
function removeFragmentSpread(doc, fieldPath, fragmentName, target) {
  return mapDefinition(doc, target, (def) => {
    const targetSet = fieldPath.length === 0 ? def.selectionSet : findSelectionSet(def.selectionSet, fieldPath);
    if (!targetSet) {
      return def;
    }
    const remaining = targetSet.selections.filter((s) => !(s.kind === Kind.FRAGMENT_SPREAD && s.name.value === fragmentName));
    if (remaining.length === targetSet.selections.length) {
      return def;
    }
    let newSelectionSet;
    if (fieldPath.length === 0) {
      newSelectionSet = {
        ...def.selectionSet,
        selections: remaining
      };
    } else if (remaining.length === 0) {
      newSelectionSet = removeField(def.selectionSet, fieldPath);
    } else {
      newSelectionSet = replaceFieldSelectionSet(def.selectionSet, fieldPath, {
        kind: Kind.SELECTION_SET,
        selections: remaining
      });
    }
    if (newSelectionSet.selections.length === 0) {
      return def.kind === Kind.OPERATION_DEFINITION ? null : def;
    }
    if (def.kind === Kind.FRAGMENT_DEFINITION) {
      return {
        ...def,
        selectionSet: newSelectionSet
      };
    }
    return pruneUnusedVariableDefinitions({
      ...def,
      selectionSet: newSelectionSet
    }, doc);
  });
}
function inlineFragment(doc, fragmentName) {
  const fragment = doc.definitions.find((d) => d.kind === Kind.FRAGMENT_DEFINITION && d.name.value === fragmentName);
  if (!fragment) {
    return doc;
  }
  const inlined = fragment.selectionSet.selections;
  const isTargetSpread = (s) => s.kind === Kind.FRAGMENT_SPREAD && s.name.value === fragmentName;
  const rewritten = visit(doc, {
    SelectionSet(node) {
      if (!node.selections.some(isTargetSpread)) {
        return;
      }
      return {
        ...node,
        selections: node.selections.flatMap((s) => isTargetSpread(s) ? inlined : [s])
      };
    }
  });
  return {
    ...rewritten,
    definitions: rewritten.definitions.filter((d) => !(d.kind === Kind.FRAGMENT_DEFINITION && d.name.value === fragmentName))
  };
}
function renameFragment(doc, oldName, newName) {
  if (!newName || oldName === newName) {
    return doc;
  }
  const names = new Set(listFragments(doc));
  if (!names.has(oldName) || names.has(newName)) {
    return doc;
  }
  const rename = (node) => {
    if (node.name.value !== oldName) {
      return;
    }
    return {
      ...node,
      name: {
        ...node.name,
        value: newName
      }
    };
  };
  return visit(doc, {
    FragmentDefinition: rename,
    FragmentSpread: rename
  });
}
export {
  createFragmentFromSelection,
  inlineFragment,
  listFragmentInfos,
  listFragments,
  removeFragmentSpread,
  renameFragment
};
//# sourceMappingURL=fragments.js.map
